import {sendEvent} from '@github-ui/hydro-analytics'
import {SUGGESTION_REASON, SUGGESTION_REASON_TEXT} from '@github-ui/item-picker-base/constants/labels'
import {isLabelArchived} from '@github-ui/label-token/RepositoryLabelToken'
import {testIdProps} from '@github-ui/test-id-props'
import {ArchiveIcon} from '@primer/octicons-react'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {SuggestedLabel} from '../../api/memex-items/contracts'
import {SanitizedHtml} from '../../components/dom/sanitized-html'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useFetchSuggestedLabels} from '../../state-providers/suggestions/use-fetch-suggested-labels'
import {useSuggestionsContext} from '../../state-providers/suggestions/use-suggestions-context'
import {useEnabledFeatures} from '../use-enabled-features'
import {useUpdateItem} from '../use-update-item'
import styles from './use-labels-editor.module.css'

export const getNameHtml = (option: SuggestedLabel) => {
  return option.nameHtml
}

/**
 * Group order is defined by `LABELS_GROUP_METADATA`, not by this map.
 */
export const LabelGroupId = {
  Selected: 'selected',
  Suggestions: 'suggestions',
  Labels: 'labels',
} as const

/**
 * Mirrors the grouping the Issues label picker uses, so the two surfaces read the
 * same way. Every group carries a title because a group declared without one
 * renders a fallback heading ("Group labels") rather than no heading at all.
 */
const LABELS_GROUP_METADATA = [
  {groupId: LabelGroupId.Selected, header: {title: 'Selected', variant: 'filled' as const}},
  {groupId: LabelGroupId.Suggestions, header: {title: 'Suggestions', variant: 'filled' as const}},
  {groupId: LabelGroupId.Labels, header: {title: 'Labels', variant: 'filled' as const}},
]

/**
 * Headings only earn their space once there is a suggestions group to separate
 * from the rest. Without suggestions — an unsupported item, a repository with no
 * signal, or the server flags off — the panel renders as the flat list it always
 * has. `useSelectPanel` drops any group that ends up with no items, so the
 * "Selected" heading appears only once something is applied.
 */
export function useLabelsGroupMetadata(model: MemexItemModel) {
  const {getSuggestedLabelsForItem} = useSuggestionsContext()
  const suggestions = getSuggestedLabelsForItem(model.getSuggestionsCacheKey())

  return useMemo(() => {
    if (!suggestions || suggestions instanceof Error) return undefined
    return suggestions.some(label => label.suggestionReason) ? LABELS_GROUP_METADATA : undefined
  }, [suggestions])
}

const LabelItemContent = ({label}: {label: SuggestedLabel}) => {
  const labelName = <SanitizedHtml className={styles.SanitizedHtml}>{label.nameHtml}</SanitizedHtml>

  return (
    <>
      {isLabelArchived(label) ? (
        <span className={styles.ArchivedLabelContent}>
          <ArchiveIcon size={12} className={styles.ArchivedLabelIcon} aria-label="Archived" />
          {labelName}
        </span>
      ) : (
        labelName
      )}
    </>
  )
}

export const convertOptionToItem = (
  option: SuggestedLabel,
  selectedLabels: Array<SuggestedLabel>,
): ItemProps & SuggestedLabel => {
  const color = `#${option.color}`
  const isSelected = selectedLabels.some(label => label.id === option.id)

  // A reason the client doesn't know yet renders no subtext rather than a raw
  // enum value: the server can ship a new suggestion source ahead of the client.
  const reasonText = option.suggestionReason ? SUGGESTION_REASON_TEXT[option.suggestionReason] : undefined

  let groupId: string = LabelGroupId.Labels
  if (isSelected) {
    groupId = LabelGroupId.Selected
  } else if (option.suggestionReason) {
    groupId = LabelGroupId.Suggestions
  }

  // The panel hands these objects back as the selected options, so the label
  // fields have to survive the round trip. The suggestion attributions do not:
  // they have already been folded into `groupId` and `description`, and Primer
  // forwards anything it doesn't recognise onto the DOM node.
  const {suggestionReason: _reason, suggestionRank: _rank, suggestionOrder: _order, ...label} = option

  return {
    ...label,
    leadingVisual() {
      return <div style={{'--option-color': color}} className={styles.Box} />
    },
    children: <LabelItemContent label={option} />,
    // The reason explains why a label is being suggested, so it is dropped once
    // the label is applied and has moved out of the suggestions group.
    ...(reasonText && !isSelected ? {description: reasonText, descriptionVariant: 'inline' as const} : {}),
    groupId,
    ...testIdProps('table-cell-editor-row'),
  }
}

/**
 * Reports selections of suggested labels using the same hydro event and payload
 * keys as the Issues label picker, so Projects lands in that funnel rather than
 * a parallel one. Only additions are reported; deselections are not.
 *
 * `reason` and `rank` carry the server's own attribution — `rank` is 0-based
 * *within* its reason, matching what the Issues picker sends. A label that was
 * not suggested reports no `rank` and falls back to the shared
 * `search_result` reason, which is exactly what the Issues picker sends for the
 * same case; emitting `undefined` instead would leave the two halves of the
 * funnel unjoinable on `reason`.
 */
export function useLabelSuggestionAnalytics(model: MemexItemModel) {
  const {memex_label_suggestions: suggestionsEnabled} = useEnabledFeatures()
  const {getSuggestedLabelsForItem} = useSuggestionsContext()
  const suggestions = getSuggestedLabelsForItem(model.getSuggestionsCacheKey())

  // The panel hands back the converted items, which no longer carry the
  // attribution, so it is resolved by id against the decorated option list.
  const attributions = useMemo(() => {
    const byId = new Map<number, SuggestedLabel>()
    if (!suggestions || suggestions instanceof Error) return byId
    for (const label of suggestions) {
      if (label.suggestionReason) byId.set(label.id, label)
    }
    return byId
  }, [suggestions])

  return useCallback(
    (added: Array<SuggestedLabel>, filterValue: string) => {
      // With the flag off Projects contributes nothing to this funnel, so the
      // ramp is a clean before/after rather than a mix of attributed and
      // unattributed rows.
      if (!suggestionsEnabled) return

      const trimmedFilter = filterValue.trim()

      for (const label of added) {
        const attribution = attributions.get(label.id)
        sendEvent('item-picker-label-suggestion-select', {
          hadSearchTerm: trimmedFilter.length > 0,
          searchTermLength: trimmedFilter.length,
          reason: attribution?.suggestionReason ?? SUGGESTION_REASON.searchResult,
          rank: attribution?.suggestionRank,
        })
      }
    },
    [attributions, suggestionsEnabled],
  )
}

type UseLabelsEditorProps = {
  model: MemexItemModel
  onSaved?: () => void
}

export function useLabelsEditor({model, onSaved}: UseLabelsEditorProps) {
  const {updateItem} = useUpdateItem()
  const {fetchSuggestedLabels} = useFetchSuggestedLabels()

  const fetchOptions = useCallback(() => {
    fetchSuggestedLabels(model)
  }, [model, fetchSuggestedLabels])

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedLabel>) => {
      await updateItem(model, {
        dataType: MemexColumnDataType.Labels,
        value: nextSelected,
      })
      if (onSaved) {
        onSaved()
      }
    },
    [model, onSaved, updateItem],
  )

  return {
    fetchOptions,
    saveSelected,
  }
}
